import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, Compass, Layers, HeartHandshake, FileText, CalendarClock,
  Wrench, Search, User, Users, LogOut, Menu, X, Lock, Sparkles, ChevronDown, Flag, Map, Settings, ExternalLink, BarChart3,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { SCENARIO_GUIDE_URL } from "@/components/SystemStrip";
import api from "@/lib/api";
import { TermsNotice } from "@/components/TermsNotice";

const TOP_NAV = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/start-here", label: "Start Here", icon: Compass },
];
const BOTTOM_NAV = [
  { to: "/app/conclusion", label: "Conclusion", icon: Flag },
  { to: "/app/templates", label: "Template Library", icon: FileText },
  { to: "/app/build-system", label: "Build Your System", icon: Wrench },
];

export default function AppShell({ children }) {
  const { user, entitlements, logout } = useAuth();
  const { part2 } = useTheme();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const loc = useLocation();

  const shellBg = part2 ? "bg-nm-mauveBg" : "bg-nm-bg";
  const shellBg80 = part2 ? "bg-nm-mauveBg/80" : "bg-nm-bg/80";
  const sidebarBg = part2 ? "bg-[#E8E1F4]" : "bg-[#E1ECE6]";
  const shellBorder = part2 ? "border-nm-plum/15" : "border-nm-primary/10";
  const searchBorder = part2 ? "border-nm-plum/15 focus:ring-nm-plum/40" : "border-nm-primary/12 focus:ring-nm-primary/40";
  const avatarBg = part2 ? "bg-nm-plum" : "bg-nm-primary";
  const navActive = part2 ? "bg-nm-green/15 text-nm-green font-medium" : "bg-nm-primary/10 text-nm-primary font-medium";
  const navHover = part2 ? "hover:bg-nm-green/[0.07] hover:text-nm-ink" : "hover:bg-nm-primary/[0.05] hover:text-nm-ink";

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/app/search?q=${encodeURIComponent(q.trim())}`);
  };

  const [sections, setSections] = useState([]);
  useEffect(() => { api.get("/content/sections").then((r) => setSections(r.data)).catch(() => {}); }, []);
  const part1Sections = sections.filter((s) => s.part === "part_1");
  const part2Sections = sections.filter((s) => s.part === "part_2");
  const openParam = new URLSearchParams(loc.search).get("open");

  const [expanded, setExpanded] = useState({ part_1: false, part_2: false });
  useEffect(() => {
    setExpanded((e) => ({
      part_1: e.part_1 || loc.pathname === "/app/part/part_1",
      part_2: e.part_2 || loc.pathname === "/app/part/part_2" || part2,
    }));
  }, [loc.pathname, part2]);

  const NavGroup = ({ part, label, icon: Icon, secs }) => {
    const locked = (part === "part_1" && !entitlements.has_part_1) || (part === "part_2" && !entitlements.has_part_2);
    const isOpen = expanded[part];
    return (
      <div>
        <button
          onClick={() => setExpanded((e) => ({ ...e, [part]: !e[part] }))}
          data-testid={`nav-group-${part}`}
          className={`w-full group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[15px] text-nm-neutral transition-colors duration-200 ${navHover}`}
        >
          <Icon size={18} strokeWidth={2} className="shrink-0" />
          <span className="flex-1 text-left">{label}</span>
          {locked && <Lock size={13} className="text-nm-neutral/60" />}
          <ChevronDown size={15} className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className={`mt-1 ml-5 pl-3 border-l space-y-0.5 ${part2 ? "border-nm-plum/15" : "border-nm-primary/12"}`}>
            {part !== "part_1" && (
              <Link to={`/app/part/${part}`} onClick={() => setOpen(false)} data-testid={`nav-section-overview-${part}`}
                className={`block rounded-lg px-3 py-1.5 text-[13px] transition-colors duration-200 ${loc.pathname === `/app/part/${part}` && !openParam ? navActive : `text-nm-neutral ${navHover}`}`}>
                Overview
              </Link>
            )}
            {secs.map((s) => {
              const active = loc.pathname === `/app/part/${part}` && openParam === s.slug;
              return (
                <Link key={s.slug} to={`/app/part/${part}?open=${s.slug}`} onClick={() => setOpen(false)}
                  data-testid={`nav-section-${s.slug}`}
                  className={`block rounded-lg px-3 py-1.5 text-[13px] leading-snug transition-colors duration-200 ${active ? navActive : `text-nm-neutral ${navHover}`}`}>
                  {s.title}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const SimpleLink = ({ item }) => {
    const Icon = item.icon;
    return (
      <NavLink to={item.to} end={item.end} onClick={() => setOpen(false)}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[15px] transition-colors duration-200 ${isActive ? navActive : `text-nm-neutral ${navHover}`}`}>
        <Icon size={18} strokeWidth={2} className="shrink-0" />
        <span className="flex-1">{item.label}</span>
      </NavLink>
    );
  };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="px-6 pt-7 pb-5">
        <NavLink to="/app" onClick={() => setOpen(false)} data-testid="sidebar-logo" className="block">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-nm-coral">Manage(Meant) Excellence</div>
          <div className="font-serif text-2xl leading-tight text-nm-primary mt-1">The Manager<br />Operating System</div>
        </NavLink>
      </div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {TOP_NAV.map((item) => <SimpleLink key={item.to} item={item} />)}
        <NavGroup part="part_1" label="Toolkit 1 · The Role" icon={Layers} secs={part1Sections} />
        <NavGroup part="part_2" label="Toolkit 2 · The People" icon={HeartHandshake} secs={part2Sections} />
        <SimpleLink item={{ to: "/app/moments", label: "Toolkit 3 · The Moments", icon: Map }} />
        {(entitlements.has_part_3 || entitlements.bundle_owner) && (
          <a href={SCENARIO_GUIDE_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}
            data-testid="nav-open-scenario-guide"
            className={`group flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[15px] transition-colors duration-200 text-[#B0851E] ${navHover}`}>
            <ExternalLink size={18} strokeWidth={2} className="shrink-0" />
            <span className="flex-1">Open Scenario Guide</span>
          </a>
        )}
        {BOTTOM_NAV.map((item) => <SimpleLink key={item.to} item={item} />)}
        {entitlements.team_owner && <SimpleLink item={{ to: "/app/team", label: "My Team", icon: Users }} />}
        {user?.is_admin && <SimpleLink item={{ to: "/app/admin/templates", label: "Manage Templates", icon: Settings }} />}
        {user?.is_admin && <SimpleLink item={{ to: "/app/admin/center", label: "Admin Center", icon: BarChart3 }} />}
      </nav>
      <div className={`p-3 border-t ${shellBorder}`}>
        {!entitlements.bundle_owner && (
          <button
            onClick={() => { setOpen(false); nav("/app/account#upgrade"); }}
            data-testid="sidebar-upgrade"
            className="w-full mb-2 flex items-center gap-2 rounded-xl bg-nm-coral px-3.5 py-2.5 text-sm font-medium text-white hover:brightness-105 transition-[filter] duration-200"
          >
            <Sparkles size={16} /> Unlock the full system
          </button>
        )}
        <NavLink to="/app/account" onClick={() => setOpen(false)} data-testid="nav-account"
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[15px] transition-colors duration-200 ${isActive ? navActive : `text-nm-neutral ${navHover}`}`}>
          <User size={18} /> <span className="flex-1">Account</span>
        </NavLink>
        <button onClick={logout} data-testid="logout-btn"
          className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[15px] text-nm-neutral transition-colors duration-200 ${navHover}`}>
          <LogOut size={18} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen nm-grain ${shellBg}`}>
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 w-[280px] border-r ${shellBorder} ${sidebarBg} z-30`}>
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-nm-ink/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className={`absolute inset-y-0 left-0 w-[280px] border-r ${shellBorder} nm-shadow-lg ${sidebarBg}`}>
            <button className="absolute top-4 right-4 text-nm-neutral" onClick={() => setOpen(false)} data-testid="close-drawer"><X size={22} /></button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-[280px]">
        {/* Top bar */}
        <header className={`sticky top-0 z-20 border-b ${shellBorder} ${shellBg80} backdrop-blur-xl`}>
          <div className="flex items-center gap-3 px-4 sm:px-8 h-16">
            <button className="lg:hidden text-nm-primary" onClick={() => setOpen(true)} data-testid="open-drawer"><Menu size={24} /></button>
            <form onSubmit={submitSearch} className="flex-1 max-w-lg relative">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-nm-neutral/60" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="topbar-search"
                placeholder="Search chapters, templates, sections…"
                className={`w-full rounded-full border bg-white/70 pl-11 pr-4 py-2.5 text-sm text-nm-ink placeholder:text-nm-neutral/55 focus:outline-none focus:ring-2 transition-shadow duration-200 ${searchBorder}`}
              />
            </form>
            <div className="hidden sm:flex items-center gap-2 ml-auto">
              <div className="text-right leading-tight">
                <div className="text-sm font-medium text-nm-ink">{user?.name}</div>
                <div className="text-[11px] text-nm-neutral">
                  {entitlements.bundle_owner ? "Full system" : entitlements.has_part_1 ? "The Role" : entitlements.has_part_2 ? "The People" : "Free preview"}
                </div>
              </div>
              <div className={`h-9 w-9 rounded-full text-white flex items-center justify-center font-medium text-sm ${avatarBg}`}>
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        <motion.main
          key={loc.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="px-4 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-[1440px]"
        >
          {children}
        </motion.main>
        <TermsNotice />
      </div>
    </div>
  );
}
